import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { KeyRound, Mail, Globe, Save } from 'lucide-react';
import { toast } from 'sonner';

const GROUPS = [
  {
    title: 'AI (OpenAI)',
    icon: KeyRound,
    desc: 'Enables AI answer explanations and AI image generation.',
    fields: [
      { key: 'OPENAI_API_KEY', label: 'OpenAI API Key', placeholder: 'sk-...', secret: true },
      { key: 'OPENAI_EXPLAIN_MODEL', label: 'Explain model', placeholder: 'gpt-4o-mini' },
    ],
  },
  {
    title: 'Email (SMTP)',
    icon: Mail,
    desc: 'Enables group invite emails and the daily digest.',
    fields: [
      { key: 'SMTP_HOST', label: 'SMTP Host', placeholder: 'smtp.hostinger.com' },
      { key: 'SMTP_PORT', label: 'SMTP Port', placeholder: '465' },
      { key: 'SMTP_USER', label: 'SMTP User', placeholder: 'no-reply@socraquest.sk' },
      { key: 'SMTP_PASSWORD', label: 'SMTP Password', placeholder: '••••••••', secret: true },
      { key: 'SMTP_FROM', label: 'From (optional)', placeholder: 'SocraQuest <no-reply@socraquest.sk>' },
    ],
  },
  {
    title: 'App',
    icon: Globe,
    desc: 'Public URL used in emails and share links.',
    fields: [
      { key: 'APP_BASE_URL', label: 'App base URL', placeholder: 'https://socraquest.sk' },
    ],
  },
];

export function AdminSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await adminAPI.getSettings();
      setSettings(res.data.settings || {});
      setEdits({});
    } catch (e) {
      toast.error('Failed to load settings');
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!Object.keys(edits).length) { toast.info('Nothing changed'); return; }
    setSaving(true);
    try {
      const res = await adminAPI.saveSettings(edits);
      setSettings(res.data.settings || {});
      setEdits({});
      toast.success('Settings saved — applied immediately');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <LoadingSpinner className="py-20" />;

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white font-['Space_Grotesk']">Settings</h1>
          <p className="text-sm text-slate-400 mt-1">API keys & integrations — saved values apply instantly, no restart needed.</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save changes'}
        </Button>
      </div>

      {GROUPS.map(group => (
        <div key={group.title} className="bg-[#0d1f35] border border-slate-800 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-1">
            <group.icon className="w-5 h-5 text-teal-400" />
            <h2 className="text-lg font-semibold text-white">{group.title}</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">{group.desc}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {group.fields.map(f => {
              const info = settings[f.key] || {};
              return (
                <div key={f.key} className={f.key === 'OPENAI_API_KEY' || f.key === 'SMTP_FROM' ? 'sm:col-span-2' : ''}>
                  <Label className="text-slate-300 flex items-center gap-2">
                    {f.label}
                    {info.set && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${info.source === 'admin' ? 'bg-teal-500/20 text-teal-300' : 'bg-slate-700 text-slate-300'}`}>
                        {info.source === 'admin' ? 'set here' : 'from server env'}
                      </span>
                    )}
                  </Label>
                  <Input
                    type={f.secret ? 'password' : 'text'}
                    placeholder={info.set ? info.value : f.placeholder}
                    value={edits[f.key] ?? ''}
                    onChange={e => setEdits({ ...edits, [f.key]: e.target.value })}
                    className="mt-1 bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-600"
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <p className="text-xs text-slate-500">
        Tip: leave a field empty to keep its current value. Values set here override the server's .env file.
      </p>
    </div>
  );
}
